/**
 * ERROR BOUNDARY COMPONENT
 *
 * Catches and handles React errors gracefully
 */

import React from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Container,
  Paper,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // You can log the error here
    console.error("Error boundary caught an error:", error, errorInfo);

    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
  }

  handleRefresh = () => {
    // Reset error state and reload page
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          bgcolor="background.default"
          p={3}
        >
          <Container maxWidth="md">
            <Paper
              elevation={3}
              sx={{
                p: 4,
                textAlign: "center",
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "error.main",
              }}
            >
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Oops! Something went wrong
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  The application encountered an unexpected error. Don't worry,
                  your data is safe.
                </Typography>
              </Alert>

              <Box sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleRefresh}
                >
                  Refresh Page
                </Button>
              </Box>

              {/* Error details (only in development) */}
              {process.env.NODE_ENV === "development" && this.state.error && (
                <Box sx={{ mt: 3, textAlign: "left" }}>
                  <Typography variant="h6" gutterBottom>
                    Error Details:
                  </Typography>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: "#1a1a1a",
                      border: "1px solid #333",
                      overflow: "auto",
                      maxHeight: 200,
                    }}
                  >
                    <Typography
                      component="pre"
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        color: "#ff6b6b",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {this.state.error.toString()}
                    </Typography>
                  </Paper>

                  {this.state.errorInfo && (
                    <Paper
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: "#1a1a1a",
                        border: "1px solid #333",
                        overflow: "auto",
                        maxHeight: 200,
                      }}
                    >
                      <Typography
                        component="pre"
                        variant="body2"
                        sx={{
                          fontSize: "0.65rem",
                          color: "#ffa726",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {this.state.errorInfo.componentStack}
                      </Typography>
                    </Paper>
                  )}
                </Box>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                If the problem persists, please contact support.
              </Typography>
            </Paper>
          </Container>
        </Box>
      );
    }

    // If no error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
